<template>
  <sticky-header-layout
    v-loading="isPageLoading"
  >
    <template #header>
      <div class="action-container">
        <div class="pagination-container">
          <el-pagination
            slot="left"
            :disabled="isLoading"
            :current-page="listQuery.page"
            :page-sizes="listQuery.sizes"
            :page-size="listQuery.size"
            :total="listQuery.total"
            background
            layout="total, sizes, pager, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </template>

    <el-table
      v-loading="isLoading"
      :data="list"
      border
      stripe
    >
      <el-table-column
        prop="order_number"
        label="Order Number"
        width="150"
      />

      <el-table-column
        prop="refund_type"
        label="Refund Type"
      />

      <el-table-column
        prop="refund_option"
        label="Refund Option"
      />

      <el-table-column
        prop="requested_at"
        label="Requested At"
        width="100"
      />

      <el-table-column
        prop="first_approved_at"
        label="1st Approved At"
        width="100"
      />

      <el-table-column
        prop="second_approved_at"
        label="2nd Approved At"
        width="100"
      />

      <el-table-column
        prop="confirmed_at"
        label="Confirmed At"
        width="100"
      />

      <el-table-column
        prop="cancelled_at"
        label="Cancelled At"
        width="100"
      />

      <el-table-column
        :label="$t('table.action')"
        fixed="right"
        align="center"
        width="120"
      >
        <template #default="{ row }">
          <div class="button-group">
            <el-button
              v-if="row.confirmed_at === null && row.cancelled_at === null"
              size="mini"
              type="warning"
              icon="el-icon-edit"
              circle
              @click="showRefundRequestDetail(row)"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>
    
    <el-dialog
      v-if="showRefundRequestDetailModal"
      :visible.sync="showRefundRequestDetailModal"
      title="Refund Request Detail"
    >
      <el-form
        class="round-border-form"
        label-position="left"
        label-width="150px"
      >
        <el-form-item label="Order Number">
          {{ selectedItem.order_number }}
        </el-form-item>
        <el-form-item label="Shopper Name">
          {{ selectedItem.shopper_name }}
        </el-form-item>
        <el-form-item label="Requested By">
          {{ selectedItem.requester_name }}
        </el-form-item>
        <el-form-item label="Total">
          {{ selectedItem.currency }} {{ selectedItem.order_total }}
        </el-form-item>
        <el-form-item label="Refund Type">
          {{ selectedItem.refund_type }}
        </el-form-item>
        <el-form-item label="Refund Option">
          {{ selectedItem.refund_option }}
        </el-form-item>
        <el-form-item label="Refund Total">
          {{ selectedItem.currency }} {{ selectedItem.refund_total }}
        </el-form-item>
      </el-form>

      <el-table
        style="margin-top: 20px;" 
        :data="selectedItem.items"
      >
        <el-table-column
          prop="brand_name"
          label="Brand Name"
        />
        <el-table-column
          prop="product_name"
          label="Product Name"
        />
        <el-table-column
          prop="quantity"
          label="Quantity"
          width="80"
        />
        <el-table-column
          prop="refund_quantity"
          label="Refund Quantity"
          width="100"
        />
        <el-table-column
          prop="currency"
          label="Currency"
          width="80"
        />
        <el-table-column
          prop="unit_price"
          label="Unit Price"
          width="100"
        />
      </el-table>

      <div style="margin-top: 20px;text-align: right;">
        <el-button
          :loading="isSaving"
          class="save-button"
          size="mini"
          type="primary"
          @click="handleConfirmRefund"
        >
          Confirm
        </el-button>
      </div>

      <div style="margin-top: 20px;text-align: right;">
        <el-button
          :loading="isSaving"
          class="save-button"
          size="mini"
          type="danger"
          @click="handleRejectRefund"
        >
          Reject
        </el-button>
      </div>
    </el-dialog>
  </sticky-header-layout>
</template>

<script>
import { createComponent, onBeforeMount, ref, reactive, computed } from '@vue/composition-api'

import ListPageHeader from '../layout/components/ListPageHeader.vue'

import PrivateClientsService from '../../api/private-clients'
import useTablePaging from '../../hooks/useTablePaging'
import { tableListHandler } from '../../utils/form'

const PrivateClientOrderRefundRequestList = createComponent({
  name: 'PrivateClientOrderRefundRequestList',
  components: {
    ListPageHeader,
  },
  setup(props, { root }) {
    const { $router } = root

    const isPageLoading = ref(false)
    const isSaving = ref(false)
    const selectedItem = reactive({})
    const showRefundRequestDetailModal = ref(false)

    const {
      isLoading,
      list,
      listQuery,
      getList,
      handleSizeChange,
      handlePageChange,
      handleQueryParamsUpdate,
    } = useTablePaging({ service: PrivateClientsService.getOrderRefundRequestList, listHandler: tableListHandler })

    onBeforeMount(() => {
      getList()
    })

    const showRefundRequestDetail = (item) => {
      Object.assign(selectedItem, item)

      root.$nextTick(() => {
        showRefundRequestDetailModal.value = true
      })
    }

    const handleConfirmRefund = () => {
      root.$confirm('Confirm Refund?', root.$t('common.warning'), {
        confirmButtonText: root.$t('button.ok'),
        cancelButtonText: root.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        processOrderRefundRequest('confirm')
      }).catch((error) => {
        console.log(error)
      })
    }

    const handleRejectRefund = () => {
      root.$confirm('Reject Refund?', root.$t('common.warning'), {
        confirmButtonText: root.$t('button.ok'),
        cancelButtonText: root.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        processOrderRefundRequest('cancel')
      }).catch((error) => {
        error
      })
    }

    const processOrderRefundRequest = async(decision) => {
      isSaving.value = true

      try {
        const payload = {
          decision: decision
        }

        await PrivateClientsService.processOrderRefundRequest(payload, selectedItem.id)

        root.$message({
          message: 'Success',
          type: 'success',
        })

        isSaving.value = false
        showRefundRequestDetailModal.value = false
        Object.assign(selectedItem, {})

        getList()
      } catch (error) {
        isSaving.value = false

        root.$alert(`Error: ${error}`, root.$t('error.somethingWentWrong'), {
          type: 'error',
          confirmButtonText: root.$t('button.ok'),
        })
      }
    }

    return {
      showRefundRequestDetail,
      showRefundRequestDetailModal,
      selectedItem,
      isSaving,
      isLoading,
      isPageLoading,
      list,
      listQuery,
      handleSizeChange,
      handlePageChange,
      handleConfirmRefund,
      handleRejectRefund,
    }
  },
})

export default PrivateClientOrderRefundRequestList

</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";

::v-deep {
  .el-tag {
    white-space: normal;
    height: auto;
    word-break: break-word;
  }
  .el-table__row {
    .cell {
      a {
        text-decoration: underline;
      }
    }
  }
}
</style>
